import React, { useEffect, useState } from "react";
import {
  Radio, Server, Webhook, FolderOpen, Loader2, CheckCircle2,
  AlertTriangle, Save, Trash2, ExternalLink, RefreshCcw, History,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PushAnalytics from "@/components/automation/PushAnalytics";
import s from "./Automation.module.css";
import { appConfirm } from "@/components/AppDialogHost";

const PROVIDER_ICON = {
  azuracast: Server,
  radio_co:  Radio,
  webhook:   Webhook,
  sftp:      FolderOpen,
};

function relative(ts) {
  if (!ts) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return new Date(ts).toLocaleDateString();
}

export default function Automation() {
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState(null);
  const [config, setConfig] = useState({});
  const [savedMask, setSavedMask] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [savedAt, setSavedAt] = useState(null);
  const [lastTest, setLastTest] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [provs, cfg, hist] = await Promise.all([
        api.get("/push-to-air/providers"),
        api.get("/push-to-air/config"),
        api.get("/push-to-air/history?limit=20"),
      ]);
      setProviders(provs.data?.providers || []);
      if (cfg.data?.configured) {
        setProvider(cfg.data.provider);
        setSavedMask(cfg.data.config || {});
        setConfig({});
        setSavedAt(cfg.data.updated_at);
        setLastTest({
          at: cfg.data.last_test_at,
          status: cfg.data.last_test_status,
          message: cfg.data.last_test_message,
        });
      }
      setHistory(hist.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load automation settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const activeSpec = providers.find((p) => p.id === provider);

  const pickProvider = (id) => {
    setProvider(id);
    setConfig({});
    setSavedMask({});
    setTestResult(null);
  };

  const fieldValue = (fieldKey) => {
    if (config[fieldKey] !== undefined) return config[fieldKey];
    const sv = savedMask[fieldKey];
    if (sv && String(sv).startsWith("••••••••")) return ""; // mask placeholder — user must re-enter to change
    return sv ?? "";
  };

  const isSecretSaved = (field) => {
    return field.kind?.startsWith("secret") && String(savedMask[field.key] || "").startsWith("••••••••");
  };

  const save = async () => {
    if (!provider) return;
    setSaving(true);
    setTestResult(null);
    try {
      const cleanCfg = {};
      Object.entries(config).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== "") cleanCfg[k] = v;
      });
      const r = await api.put("/push-to-air/config", { provider, config: cleanCfg });
      setSavedMask(r.data?.config || {});
      setConfig({});
      setSavedAt(r.data?.updated_at);
      toast.success("Automation configuration saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    if (!provider) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/push-to-air/test", {});
      setTestResult(r.data);
      setLastTest({ at: new Date().toISOString(), status: r.data?.ok ? "ok" : "fail", message: r.data?.message });
      if (r.data?.ok) toast.success("Connection OK");
      else toast.error(r.data?.message || "Test failed");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Test failed";
      setTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!await appConfirm("Remove automation configuration?")) return;
    try {
      await api.delete("/push-to-air/config");
      setProvider(null);
      setConfig({});
      setSavedMask({});
      setSavedAt(null);
      setLastTest(null);
      setTestResult(null);
      toast.success("Automation removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className={s.page} data-testid="automation-page">
      <div className={s.header}>
        <div>
          <div className={s.title}>Radio automation · Push to Air</div>
          <div className={s.subtitle}>Connect your station&apos;s playout system so AI-generated drops land in the cart wall in one click.</div>
        </div>
        {provider && (
          <div className={`${s.statusPill} ${lastTest?.status === "ok" ? s.statusOk : lastTest?.status === "fail" ? s.statusFail : ""}`}
               data-testid="automation-status-pill">
            {lastTest?.status === "ok" && <CheckCircle2 size={11} />}
            {lastTest?.status === "fail" && <AlertTriangle size={11} />}
            {lastTest?.status === "ok" ? "Connection healthy" : lastTest?.status === "fail" ? "Last test failed" : "Untested"}
            {lastTest?.at ? ` · ${relative(lastTest.at)}` : ""}
          </div>
        )}
      </div>

      {/* Push Analytics widget */}
      <PushAnalytics />

      {/* Provider picker */}
      <div className={s.providerGrid} data-testid="automation-provider-grid">
        {providers.map((p) => {
          const Icon = PROVIDER_ICON[p.id] || Radio;
          const isActive = provider === p.id;
          return (
            <div key={p.id}
              onClick={() => pickProvider(p.id)}
              className={`${s.providerCard} ${isActive ? s.providerCardActive : ""}`}
              data-testid={`automation-provider-${p.id}`}>
              <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
                <Icon size={16} color={isActive ? "#4a9eff" : "#c8ccd8"} />
                <div className={s.providerLabel}>{p.label}</div>
              </div>
              <div className={s.providerSummary}>{p.summary}</div>
              {!p.push_supported && (
                <div className={s.providerWarn}>
                  <AlertTriangle size={10} style={{ marginTop: 2, flexShrink: 0 }} />
                  Status-only — uploads not supported by this provider&apos;s public API.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Config form */}
      {activeSpec && (
        <div className={s.formCard} data-testid="automation-config-form">
          <div className={s.formTitle}>{activeSpec.label} configuration</div>
          <div className={s.formDesc}>
            {activeSpec.summary}
            {activeSpec.docs_url && (
              <>{" "}<a href={activeSpec.docs_url} target="_blank" rel="noreferrer"
                style={{ color: "#4a9eff", display: "inline-flex", alignItems: "center", gap: 3 }}
                data-testid="automation-docs-link">
                Docs <ExternalLink size={9} />
              </a></>
            )}
          </div>

          {(activeSpec.fields || []).map((f) => (
            <div key={f.key} className={s.fieldRow}>
              <label className={s.fieldLabel}>
                {f.label}{f.required && <span className={s.fieldRequired}>*</span>}
              </label>
              {f.kind === "secret_long" ? (
                <textarea
                  className={`${s.input} ${s.textarea}`}
                  placeholder={isSecretSaved(f) ? `Stored — ${savedMask[f.key]}` : f.placeholder}
                  value={fieldValue(f.key)}
                  onChange={(e) => setConfig((p) => ({ ...p, [f.key]: e.target.value }))}
                  data-testid={`automation-field-${f.key}`}
                />
              ) : (
                <input
                  type={f.kind === "secret" ? "password" : f.kind === "int" ? "number" : "text"}
                  className={s.input}
                  placeholder={isSecretSaved(f) ? `Stored — ${savedMask[f.key]}` : f.placeholder}
                  value={fieldValue(f.key)}
                  onChange={(e) => setConfig((p) => ({ ...p, [f.key]: e.target.value }))}
                  data-testid={`automation-field-${f.key}`}
                />
              )}
              {f.help && <div className={s.fieldHelp}>{f.help}</div>}
              {isSecretSaved(f) && !config[f.key] && (
                <div className={s.fieldHelp} style={{ color: "#3ecf8e" }}>
                  Currently stored — leave blank to keep it, or paste a new value to replace.
                </div>
              )}
            </div>
          ))}

          <div className={s.actions}>
            <button type="button" className={s.btn} onClick={save} disabled={saving || !provider}
              data-testid="automation-save-btn">
              {saving ? <Loader2 size={12} className={s.spin} /> : <Save size={12} />}
              {saving ? "Saving…" : (Object.keys(savedMask).length ? "Update" : "Save")}
            </button>
            <button type="button" className={`${s.btn} ${s.btnGhost}`} onClick={test}
              disabled={testing || !Object.keys(savedMask).length}
              data-testid="automation-test-btn">
              {testing ? <Loader2 size={12} className={s.spin} /> : <RefreshCcw size={12} />}
              {testing ? "Testing…" : "Test connection"}
            </button>
            {Object.keys(savedMask).length > 0 && (
              <button type="button" className={`${s.btn} ${s.btnDanger}`} onClick={remove}
                data-testid="automation-delete-btn">
                <Trash2 size={12} /> Remove
              </button>
            )}
          </div>

          {testResult && (
            <div className={`${s.testBox} ${testResult.ok ? s.testBoxOk : s.testBoxFail}`}
              data-testid="automation-test-result">
              {testResult.ok ? <CheckCircle2 size={12} style={{ display: "inline", marginRight: 5 }} />
                             : <AlertTriangle size={12} style={{ display: "inline", marginRight: 5 }} />}
              {testResult.message}
              {testResult.info?.matched_station && (
                <div style={{ marginTop: 4, opacity: 0.85 }}>Matched station: <b>{testResult.info.matched_station}</b></div>
              )}
              {testResult.info?.track && (
                <div style={{ marginTop: 4, opacity: 0.85 }}>Now playing: <b>{testResult.info.track}</b></div>
              )}
            </div>
          )}
          {savedAt && <div className={s.fieldHelp} style={{ marginTop: 10 }}>Last saved {relative(savedAt)}</div>}
        </div>
      )}

      {/* History */}
      <div style={{ marginTop: 6 }}>
        <div className={s.formTitle} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <History size={13} /> Recent pushes
        </div>
        {history.length === 0 ? (
          <div className={`${s.historyTable} ${s.historyEmpty}`} data-testid="automation-history-empty">
            Nothing pushed yet. Generate an artist drop and hit <b>Push to Air</b> on the drop card.
          </div>
        ) : (
          <table className={s.historyTable} data-testid="automation-history">
            <thead>
              <tr><th>When</th><th>Asset</th><th>Provider</th><th>Status</th><th>Detail</th></tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i} data-testid={`automation-history-row-${i}`}>
                  <td>{relative(h.started_at)}</td>
                  <td>{h.asset_name}</td>
                  <td>{h.provider}</td>
                  <td className={h.status === "ok" ? s.historyStatusOk : s.historyStatusFail}>{h.status}</td>
                  <td style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={h.message}>{h.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {loading && (
        <div style={{ position: "absolute", top: 12, right: 24, fontSize: 10, color: "#4a5068" }}>
          <Loader2 size={11} className={s.spin} /> loading…
        </div>
      )}
    </div>
  );
}
